import { apiRequest } from "@/shared/lib/apiClient";
import { getStoredAccountType } from "../auth";

export type DisputeStatusApi = "inProgress" | "resolved" | "rejected" | "closed";

export type DisputeApi = {
  id: number;
  display_id: number;
  reference: string;
  title?: string;
  description?: string;
  invoice_id: number;
  invoice: string;
  hospital_id: number;
  hospital: string;
  company_id: number;
  company: string;
  amount: number;
  status: DisputeStatusApi;
  status_raw?: string;
  created_at?: string;
  submitted_date: string;
  createdAt: string;
  comments?: DisputeCommentApi[];
};

export type DisputeCommentApi = {
  id: number;
  user_id: number;
  comment: string;
  author: string;
  created_at: string;
};

export type DisputeInvoiceOptionApi = {
  id: number;
  label: string;
};

export type DisputesQuery = {
  status?: DisputeStatusApi | string;
  q?: string;
};

export type CreateDisputePayload = {
  invoice_id: number;
  description: string;
  title?: string;
};

function buildQuery(query: DisputesQuery = {}): string {
  const params = new URLSearchParams();
  if (query.status) params.set("status", query.status);
  if (query.q) params.set("q", query.q);
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

/** Hospital */

export async function fetchHospitalDisputes(
  query: DisputesQuery = {},
): Promise<{ total: number; disputes: DisputeApi[] }> {
  return apiRequest<{ total: number; disputes: DisputeApi[] }>(
    `/hospital/disputes${buildQuery(query)}`,
    { auth: true },
  );
}

export async function fetchHospitalDisputeInvoiceOptions(): Promise<DisputeInvoiceOptionApi[]> {
  const response = await apiRequest<{ invoices: DisputeInvoiceOptionApi[] }>(
    "/hospital/disputes/invoices",
    { auth: true },
  );
  return response.invoices ?? [];
}

export async function fetchHospitalDispute(id: number): Promise<DisputeApi> {
  const response = await apiRequest<{ dispute: DisputeApi }>(`/hospital/disputes/${id}`, {
    auth: true,
  });
  return response.dispute;
}

export async function createHospitalDispute(payload: CreateDisputePayload): Promise<DisputeApi> {
  const response = await apiRequest<{ dispute: DisputeApi; message?: string }>(
    "/hospital/disputes",
    { method: "POST", auth: true, body: payload },
  );
  return response.dispute;
}

export async function commentHospitalDispute(id: number, comment: string): Promise<DisputeApi> {
  const response = await apiRequest<{ dispute: DisputeApi; message?: string }>(
    `/hospital/disputes/${id}/comments`,
    { method: "POST", auth: true, body: { comment } },
  );
  return response.dispute;
}

export async function updateHospitalDisputeStatus(
  id: number,
  status: DisputeStatusApi | string,
): Promise<DisputeApi> {
  const response = await apiRequest<{ dispute: DisputeApi; message?: string }>(
    `/hospital/disputes/${id}/status`,
    { method: "PATCH", auth: true, body: { status } },
  );
  return response.dispute;
}

/** Auditor / admin */

function disputesBasePath(): "/auditor/disputes" | "/admin/disputes" {
  return getStoredAccountType() === "admin" ? "/admin/disputes" : "/auditor/disputes";
}

export async function fetchAuditorDisputes(
  query: DisputesQuery = {},
): Promise<{ total: number; disputes: DisputeApi[] }> {
  return apiRequest<{ total: number; disputes: DisputeApi[] }>(
    `${disputesBasePath()}${buildQuery(query)}`,
    { auth: true },
  );
}

export async function fetchAuditorDispute(id: number): Promise<DisputeApi> {
  const response = await apiRequest<{ dispute: DisputeApi }>(`${disputesBasePath()}/${id}`, {
    auth: true,
  });
  return response.dispute;
}

export async function commentAuditorDispute(id: number, comment: string): Promise<DisputeApi> {
  const response = await apiRequest<{ dispute: DisputeApi; message?: string }>(
    `${disputesBasePath()}/${id}/comments`,
    { method: "POST", auth: true, body: { comment } },
  );
  return response.dispute;
}

export async function updateAuditorDisputeStatus(
  id: number,
  status: DisputeStatusApi | string,
): Promise<DisputeApi> {
  const response = await apiRequest<{ dispute: DisputeApi; message?: string }>(
    `${disputesBasePath()}/${id}/status`,
    { method: "PATCH", auth: true, body: { status } },
  );
  return response.dispute;
}
